import * as React from 'react';
import { ChangeEvent, useEffect, useState } from 'react';
import { Image } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ITotalPlatforms } from '../../../../../../../../model/platform/ITotalPlatforms';
import { CustomImageHandler } from '../../../../../../../../shared/components/CustomImageHandler';
import {
    calculateAvgReach,
    getCpmValue,
    getOldPricePerPlatform,
    getPostsPerPlatform,
    getPricePerPlatform,
} from '../../../../../../../../shared/functions/Functions';
import { setOfferItems } from '../../../../../../../../store/brand/offerBrandSlice';
import { IAllStates } from '../../../../../../../../store/rootReducer';
import { numberFormatter } from '../../../../../../../../utils/helpers/numberFormater';
import { priceSplitter } from '../../../../../../../../utils/helpers/priceSplitter';
import Flag from '../../../../../../../../shared/components/flag/Flag';
import SocialPlatformOfferItem from '../../../../../../../../shared/components/offer/SocialPlatformOfferItem';

interface IPlatformAccordionProps {
    platform: ITotalPlatforms;
}

const PlatformAccordion = ({ platform }: IPlatformAccordionProps) => {
    const { initialOffer, offerModalStatus, isCharityOrPrivate, isCharity } = useSelector(
        (state: IAllStates) => state.offerBrand
    );
    const [expended, setExpended] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        setExpended(offerModalStatus === 'negotiate');
    }, [offerModalStatus]);

    function deleteOfferItem(socialPlatformId: number) {
        const initialOfferItemCopy = [...initialOffer.offerItems];
        const selectedOfferItem = initialOfferItemCopy.find(item => item.socialPlatformId === socialPlatformId);
        const index = selectedOfferItem ? initialOfferItemCopy.indexOf(selectedOfferItem) : 0;
        initialOfferItemCopy[index] = { ...initialOfferItemCopy[index], nbPosts: 0, price: 0 };
        dispatch(setOfferItems(initialOfferItemCopy));
    }

    function changeHandlerOfferItem(
        socialPlatformId: number,
        event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        nbPosts?: number
    ) {
        const value = event && Number(event.target.value);
        const initialOfferItemCopy = [...initialOffer.offerItems];
        const selectedOfferItem = initialOfferItemCopy.find(item => item.socialPlatformId === socialPlatformId);
        const index = selectedOfferItem ? initialOfferItemCopy.indexOf(selectedOfferItem) : 0;
        if (selectedOfferItem) {
            if (nbPosts === 0) {
                deleteOfferItem(socialPlatformId);
            } else {
                initialOfferItemCopy[index] = {
                    ...initialOfferItemCopy[index],
                    price: value === undefined ? initialOfferItemCopy[index].price : value,
                    nbPosts: nbPosts === undefined ? initialOfferItemCopy[index].nbPosts : nbPosts,
                };
                dispatch(setOfferItems(initialOfferItemCopy));
            }
        }
    }

    const isStrikeTrough = getPostsPerPlatform(initialOffer.offerItems, platform.id) === 0;

    return (
        <>
            <tr className="cursor-pointer" onClick={() => setExpended(!expended)}>
                <td>
                    <div className="d-flex align-items-center">
                        <CustomImageHandler altTag={platform?.name ?? ''} photoPath={platform?.path ?? null} />
                        <span
                            className={`text-primary font-weight-bold pl-1 mb-0 ${
                                isStrikeTrough ? 'text-line-through' : ''
                            }`}
                        >
                            x
                            {offerModalStatus === 'preview'
                                ? platform.nbPostsTotal
                                : getPostsPerPlatform(initialOffer.offerItems, platform.id)}
                        </span>
                        {expended ? (
                            <Image className="ml-2" alt="image-down" src="/icons/img_arrow_up.svg" />
                        ) : (
                            <Image className="ml-2" alt="image-down" src="/icons/img_arrow_down.svg" />
                        )}
                    </div>
                </td>
                <td className="text-center">
                    <p className={isStrikeTrough ? 'text-line-through' : ''}>
                        {numberFormatter(platform.totalFollowers)}
                    </p>
                </td>
                <td className="text-center">
                    <p className={isStrikeTrough ? 'text-line-through' : ''}>
                        {numberFormatter(+calculateAvgReach(initialOffer?.offerItems, platform)?.toFixed(2))}
                    </p>
                </td>
                <td>
                    <div className={`d-flex justify-content-center ${isStrikeTrough ? 'text-line-through' : ''}`}>
                        {platform?.audiences.map(country => (
                            <div key={country.id} className="d-flex pl-2 align-items-center">
                                <Flag className="rounded" code={country?.metric} width={30} />
                                <div className="d-flex flex-column pl-2">
                                    <p className="text-primary font-weight-bold mb-0">{Math.round(country.value)}%</p>
                                    <p className="text-secondary mb-0">{numberFormatter(country.followers)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </td>
                <td className={`${isCharity ? 'd-none' : 'text-center'} ${isStrikeTrough ? 'text-line-through' : ''}`}>
                    <p className="euro-symbol">{getCpmValue(initialOffer.offerItems, platform)}</p>
                </td>
                <td className={`${isCharity ? 'd-none' : 'text-end'} ${isStrikeTrough ? 'text-line-through' : ''}`}>
                    <div className="d-flex align-items-center justify-content-end">
                        {getOldPricePerPlatform(initialOffer.offerItems, platform.id) > 0 && (
                            <p className="euro-symbol text-line-through mr-2">
                                {priceSplitter(
                                    Math.round(Math.round(getOldPricePerPlatform(initialOffer.offerItems, platform.id)))
                                )}
                            </p>
                        )}
                        <p className="euro-symbol">
                            {priceSplitter(Math.round(getPricePerPlatform(initialOffer.offerItems, platform.id)))}
                        </p>
                    </div>
                </td>
            </tr>
            {platform?.offerItems?.map(item => (
                <SocialPlatformOfferItem
                    key={item.id}
                    item={item}
                    initialOfferItems={initialOffer?.offerItems ?? []}
                    isCharity={isCharity}
                    isCharityOrPrivate={isCharityOrPrivate}
                    offerModalStatus={offerModalStatus}
                    expended={expended}
                    deleteOfferItem={deleteOfferItem}
                    changeHandlerOfferItem={changeHandlerOfferItem}
                    isInfluencerOrAgency={false}
                />
            ))}
        </>
    );
};

export default PlatformAccordion;
